import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  Button, 
  Form, 
  InputNumber, 
  DatePicker, 
  Input, 
  message, 
  Spin, 
  Tag, 
  Divider,
  Image,
  Row,
  Col,
  Space,
  Alert
} from 'antd';
import {
  DollarOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  HomeOutlined,
  MessageOutlined,
  SendOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  FileImageOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  PhoneOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { getApiUrl } from '../lib/api/config';
import Logo from '../components/general/Logo';
import dayjs from 'dayjs';

const { TextArea } = Input;

const VendorBriefingPortal = () => {
  const { vendorId, jobId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // State Management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [existingBid, setExistingBid] = useState(null);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  
  const chatEndRef = useRef(null);
  const apiUrl = getApiUrl();

  // Fetch Portal Data
  useEffect(() => {
    fetchPortalData();
  }, [vendorId, jobId]);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${apiUrl}/vendor-portal/${vendorId}/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Job not found or access denied');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to view this job');
        }
        throw new Error('Failed to load job details');
      }

      const data = await response.json();
      
      setJobData(data.job);
      setVendorData(data.vendor);
      setExistingBid(data.existingBid);
      setCompletionData(data.completionData);
      setChatMessages(data.vendorChatHistory || []);
      
      // Pre-fill form if existing bid
      if (data.existingBid) {
        form.setFieldsValue({
          bidAmount: data.existingBid.bidAmount,
          estimatedStart: data.existingBid.estimatedStart ? dayjs(data.existingBid.estimatedStart) : null,
          timeline: data.existingBid.timeline,
          notes: data.existingBid.notes
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Portal data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Submit Bid
  const handleSubmitBid = async (values) => {
    try {
      setSubmitting(true);
      
      const bidData = {
        vendorId,
        jobId,
        bidAmount: values.bidAmount,
        estimatedStart: values.estimatedStart ? values.estimatedStart.toISOString() : null,
        timeline: values.timeline,
        notes: values.notes || '',
        vendorName: vendorData?.name || vendorData?.companyName,
        vendorEmail: vendorData?.email,
        vendorPhone: vendorData?.phone
      };

      const response = await fetch(`${apiUrl}/vendor-portal/${vendorId}/${jobId}/submit-bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bidData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit bid');
      }

      const result = await response.json();
      
      message.success('Bid submitted successfully!');
      setExistingBid(result.bid);
      
      // Redirect to success page or refresh
      setTimeout(() => {
        navigate(`/vendor-portal/${vendorId}/${jobId}/success`);
      }, 1500);
      
    } catch (err) {
      message.error(err.message);
      console.error('Bid submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Send Chat Message
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    
    try {
      setChatLoading(true);
      
      const response = await fetch(`${apiUrl}/vendor-portal/${vendorId}/${jobId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: chatInput.trim(),
          role: 'vendor'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      // Add vendor message
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'vendor',
        content: chatInput.trim(),
        timestamp: new Date().toISOString()
      }]);
      
      setChatInput('');
      
      // Add AI response if provided
      if (data.aiResponse) {
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            id: (Date.now() + 1).toString(),
            role: 'ai',
            content: data.aiResponse,
            timestamp: new Date().toISOString()
          }]);
        }, 1000);
      }
      
    } catch (err) {
      message.error('Failed to send message');
      console.error('Chat error:', err);
    } finally {
      setChatLoading(false);
    }
  };

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
        <LoadingText>Loading job briefing...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Alert
          type="error"
          message="Access Denied"
          description={error}
          showIcon
          action={
            <Button onClick={() => navigate('/')}>
              Return Home
            </Button>
          }
        />
      </ErrorContainer>
    );
  }

  const formatCurrency = (amount) => `$${amount?.toLocaleString() || 0}`;
  const getCompletionColor = (percentage) => {
    if (percentage >= 100) return '#52c41a';
    if (percentage >= 75) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <PortalContainer>
      <Header>
        <HeaderLeft>
          <Logo />
          <SystemTitle>VENDOR BRIEFING PORTAL</SystemTitle>
        </HeaderLeft>
        <HeaderRight>
          <VendorInfo>
            <VendorName>{vendorData?.name || vendorData?.companyName}</VendorName>
            <VendorId>ID: {vendorId?.slice(-8)}</VendorId>
          </VendorInfo>
        </HeaderRight>
      </Header>

      <MainContent>
        {/* Job Status & Completion */}
        <StatusBar>
          <StatusItem>
            <StatusLabel>JOB STATUS</StatusLabel>
            <StatusValue status={jobData?.status}>
              {jobData?.status?.toUpperCase() || 'ACTIVE'}
            </StatusValue>
          </StatusItem>
          <StatusItem>
            <StatusLabel>COMPLETION</StatusLabel>
            <CompletionMeter>
              <MeterFill 
                width={completionData?.percentage || 0}
                color={getCompletionColor(completionData?.percentage || 0)}
              />
              <MeterText>{completionData?.percentage || 0}%</MeterText>
            </CompletionMeter>
          </StatusItem>
          <StatusItem>
            <StatusLabel>URGENCY</StatusLabel>
            <UrgencyTag level={jobData?.urgency || 'standard'}>
              {jobData?.urgency?.toUpperCase() || 'STANDARD'}
            </UrgencyTag>
          </StatusItem>
        </StatusBar>

        <ContentGrid>
          {/* Left Column: Job Details */}
          <LeftColumn>
            {/* Surgical Packet */}
            <SurgicalPacket>
              <SectionTitle>
                <ToolOutlined /> SURGICAL PACKET
              </SectionTitle>
              
              {/* High-Res Gallery */}
              {jobData?.images && jobData.images.length > 0 && (
                <PhotoSection>
                  <SubTitle>
                    <FileImageOutlined /> High-Resolution Gallery ({jobData.images.length})
                  </SubTitle>
                  <PhotoGrid>
                    {jobData.images.map((image, idx) => (
                      <PhotoCard key={idx}>
                        <Image
                          src={image.url || image}
                          alt={`Job Photo ${idx + 1}`}
                          style={{ width: '100%', borderRadius: '6px' }}
                          placeholder
                          preview={{
                            mask: <div><EyeOutlined /> View</div>
                          }}
                        />
                        <PhotoMeta>
                          Photo {idx + 1}
                          {image.description && <div>{image.description}</div>}
                        </PhotoMeta>
                      </PhotoCard>
                    ))}
                  </PhotoGrid>
                </PhotoSection>
              )}

              {/* Technical Spec Sheet */}
              <SpecSection>
                <SubTitle>
                  <InfoCircleOutlined /> Technical Specification
                </SubTitle>
                <SpecGrid>
                  <SpecItem>
                    <SpecLabel>Category:</SpecLabel>
                    <SpecValue>{jobData?.category || 'General Repair'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Scope:</SpecLabel>
                    <SpecValue>{jobData?.title || jobData?.description}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Location:</SpecLabel>
                    <SpecValue>
                      <EnvironmentOutlined /> {jobData?.address || jobData?.zipCode || 'On-site'}
                    </SpecValue>
                  </SpecItem>
                  {jobData?.propertyType && (
                    <SpecItem>
                      <SpecLabel>Property Type:</SpecLabel>
                      <SpecValue>{jobData.propertyType}</SpecValue>
                    </SpecItem>
                  )}
                  {jobData?.accessInstructions && (
                    <SpecItem>
                      <SpecLabel>Access:</SpecLabel>
                      <SpecValue>{jobData.accessInstructions}</SpecValue>
                    </SpecItem>
                  )}
                </SpecGrid>
              </SpecSection>

              {/* Surgical Detail */}
              <DetailSection>
                <SubTitle>Surgical Detail</SubTitle>
                <DetailText>
                  {jobData?.detailedDescription || jobData?.description || 
                   'Complete surgical detail will be provided upon bid acceptance.'}
                </DetailText>
                
                {completionData?.missing && completionData.missing.length > 0 && (
                  <MissingInfo>
                    <Alert
                      type="warning"
                      message="Additional Information Needed"
                      description={
                        <div>
                          Missing details: {completionData.missing.join(', ')}
                          <br />
                          <small>These will be collected before work begins.</small>
                        </div>
                      }
                      showIcon
                      size="small"
                    />
                  </MissingInfo>
                )}
              </DetailSection>
            </SurgicalPacket>

            {/* Vendor Support Chat */}
            <ChatSection>
              <SectionTitle>
                <MessageOutlined /> Clumse Vendor Support
              </SectionTitle>
              <ChatContainer>
                <ChatMessages>
                  {chatMessages.length === 0 ? (
                    <ChatWelcome>
                      <div>👋 Welcome! Ask me anything about this job:</div>
                      <ul>
                        <li>Access details and parking</li>
                        <li>Material specifications</li>
                        <li>Timeline constraints</li>
                        <li>Property access requirements</li>
                      </ul>
                    </ChatWelcome>
                  ) : (
                    chatMessages.map((msg) => (
                      <ChatMessage key={msg.id} role={msg.role}>
                        <MessageHeader>
                          <MessageRole>
                            {msg.role === 'vendor' ? 'You' : 'Clumse AI'}
                          </MessageRole>
                          <MessageTime>
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </MessageTime>
                        </MessageHeader>
                        <MessageContent>{msg.content}</MessageContent>
                      </ChatMessage>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </ChatMessages>
                <ChatInput>
                  <Input.Group compact>
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Ask about job details, access, materials..."
                      onPressEnter={handleSendMessage}
                      disabled={chatLoading}
                      style={{ width: 'calc(100% - 50px)' }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSendMessage}
                      loading={chatLoading}
                      style={{ width: '50px' }}
                    />
                  </Input.Group>
                </ChatInput>
              </ChatContainer>
            </ChatSection>
          </LeftColumn>

          {/* Right Column: Bid Submission */}
          <RightColumn>
            <BidSection>
              <SectionTitle>
                <DollarOutlined /> {existingBid ? 'Update Bid' : 'Submit Binding Bid'}
              </SectionTitle>
              
              {existingBid && (
                <ExistingBidAlert>
                  <Alert
                    type="info"
                    message="Bid Previously Submitted"
                    description={`Current bid: ${formatCurrency(existingBid.bidAmount)} • Status: ${existingBid.status?.toUpperCase()}`}
                    showIcon
                  />
                </ExistingBidAlert>
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmitBid}
                disabled={submitting}
              >
                <Form.Item
                  label="Total Project Price"
                  name="bidAmount"
                  rules={[
                    { required: true, message: 'Bid amount is required' },
                    { type: 'number', min: 1, message: 'Amount must be greater than 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    prefix="$"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="Enter your bid amount"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Estimated Start Date"
                  name="estimatedStart"
                  rules={[{ required: true, message: 'Start date is required' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    size="large"
                    placeholder="When can you start?"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Form.Item>

                <Form.Item
                  label="Estimated Duration"
                  name="timeline"
                  rules={[{ required: true, message: 'Timeline is required' }]}
                >
                  <Input
                    placeholder="e.g., '2-3 days', '1 week', '3-4 hours'"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Notes for Homeowner"
                  name="notes"
                >
                  <TextArea
                    rows={4}
                    placeholder="Additional details, materials included, warranty info, etc."
                    maxLength={500}
                    showCount
                  />
                </Form.Item>

                <Divider />

                <Form.Item style={{ marginBottom: 0 }}>
                  <SubmitButton
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    size="large"
                    block
                    icon={<CheckCircleOutlined />}
                  >
                    {existingBid ? 'Update Bid' : 'Submit Binding Bid'}
                  </SubmitButton>
                </Form.Item>

                <DisclaimerText>
                  By submitting this bid, you agree to complete the work as specified
                  for the quoted amount and timeline.
                </DisclaimerText>
              </Form>
            </BidSection>

            {/* Job Context */}
            <ContextSection>
              <SectionTitle>Job Context</SectionTitle>
              <ContextGrid>
                <ContextItem>
                  <ContextLabel>
                    <CalendarOutlined /> Posted
                  </ContextLabel>
                  <ContextValue>
                    {jobData?.createdAt ? 
                      new Date(jobData.createdAt).toLocaleDateString() : 
                      'Recently'
                    }
                  </ContextValue>
                </ContextItem>
                
                <ContextItem>
                  <ContextLabel>
                    <HomeOutlined /> Property Owner
                  </ContextLabel>
                  <ContextValue>
                    {jobData?.landlordName || 'Property Manager'}
                  </ContextValue>
                </ContextItem>

                {jobData?.urgency && (
                  <ContextItem>
                    <ContextLabel>
                      <ClockCircleOutlined /> Timeline
                    </ContextLabel>
                    <ContextValue>
                      {jobData.urgency === 'urgent' ? 'ASAP' : 'Flexible'}
                    </ContextValue>
                  </ContextItem>
                )}

                {jobData?.estimatedBudget && (
                  <ContextItem>
                    <ContextLabel>
                      <DollarOutlined /> Est. Budget
                    </ContextLabel>
                    <ContextValue>
                      {formatCurrency(jobData.estimatedBudget)}
                    </ContextValue>
                  </ContextItem>
                )}
              </ContextGrid>
            </ContextSection>
          </RightColumn>
        </ContentGrid>
      </MainContent>
    </PortalContainer>
  );
};

// Styled Components with Mission Control aesthetic
const PortalContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0f1c 0%, #1a1f2e 100%);
  color: #e2e8f0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  border-bottom: 1px solid #475569;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const SystemTitle = styled.h1`
  font-family: 'IBM Plex Mono', 'SF Mono', Consolas, monospace;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: 1px;
`;

const VendorInfo = styled.div`
  text-align: right;
`;

const VendorName = styled.div`
  font-weight: 600;
  color: #e2e8f0;
  font-size: 14px;
`;

const VendorId = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const MainContent = styled.main`
  padding: 24px;
`;

const StatusBar = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusLabel = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const StatusValue = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${props => {
    switch (props.status) {
      case 'active': return '#10b981';
      case 'urgent': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#e2e8f0';
    }
  }};
`;

const CompletionMeter = styled.div`
  position: relative;
  width: 120px;
  height: 16px;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 8px;
  overflow: hidden;
`;

const MeterFill = styled.div`
  height: 100%;
  width: ${props => Math.min(props.width, 100)}%;
  background: ${props => props.color};
  transition: width 0.3s ease;
`;

const MeterText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: #f1f5f9;
  font-weight: 700;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
`;

const UrgencyTag = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${props => {
    switch (props.level) {
      case 'urgent': return 'rgba(239, 68, 68, 0.2)';
      case 'high': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(16, 185, 129, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.level) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      default: return '#10b981';
    }
  }};
  border: 1px solid ${props => {
    switch (props.level) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      default: return '#10b981';
    }
  }};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SurgicalPacket = styled(Card)`
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 20px 0;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .anticon {
    color: #3b82f6;
  }
`;

const SubTitle = styled.h3`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  color: #94a3b8;
  margin: 20px 0 12px 0;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PhotoSection = styled.div`
  margin-bottom: 24px;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const PhotoCard = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 8px;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    border-color: #3b82f6;
  }
`;

const PhotoMeta = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #64748b;
  text-align: center;
`;

const SpecSection = styled.div`
  margin-bottom: 24px;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
`;

const SpecItem = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 12px;
`;

const SpecLabel = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
`;

const SpecValue = styled.div`
  font-size: 13px;
  color: #e2e8f0;
  line-height: 1.4;
`;

const DetailSection = styled.div`
  margin-top: 20px;
`;

const DetailText = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #e2e8f0;
`;

const MissingInfo = styled.div`
  margin-top: 12px;
`;

const ChatSection = styled(Card)`
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

const ChatContainer = styled.div`
  height: 400px;
  display: flex;
  flex-direction: column;
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid #334155;
  border-radius: 6px;
  margin-bottom: 12px;
`;

const ChatWelcome = styled.div`
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  
  ul {
    margin: 8px 0 0 16px;
    color: #64748b;
  }
  
  li {
    margin: 4px 0;
  }
`;

const ChatMessage = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  background: ${props => props.role === 'vendor' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)'};
  border-left: 3px solid ${props => props.role === 'vendor' ? '#3b82f6' : '#10b981'};
  border-radius: 0 6px 6px 0;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const MessageRole = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
`;

const MessageTime = styled.div`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: #475569;
`;

const MessageContent = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: #e2e8f0;
`;

const ChatInput = styled.div`
  .ant-input {
    background: rgba(15, 23, 42, 0.7);
    border-color: #334155;
    color: #e2e8f0;
    
    &::placeholder {
      color: #64748b;
    }
    
    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
  }
`;

const BidSection = styled(Card)`
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 24px;
  }
  
  .ant-form-item-label > label {
    color: #e2e8f0;
    font-weight: 600;
  }
  
  .ant-input, .ant-input-number, .ant-picker {
    background: rgba(15, 23, 42, 0.7);
    border-color: #334155;
    color: #e2e8f0;
    
    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
  }
  
  .ant-input::placeholder {
    color: #64748b;
  }
`;

const ExistingBidAlert = styled.div`
  margin-bottom: 20px;
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  font-weight: 600;
  height: 48px;
  
  &:hover {
    background: linear-gradient(90deg, #2563eb 0%, #1e40af 100%);
  }
`;

const DisclaimerText = styled.div`
  font-size: 11px;
  color: #64748b;
  text-align: center;
  margin-top: 12px;
  line-height: 1.4;
`;

const ContextSection = styled(Card)`
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 20px;
  }
`;

const ContextGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContextItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #334155;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ContextLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ContextValue = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #e2e8f0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(180deg, #0a0f1c 0%, #1a1f2e 100%);
`;

const LoadingText = styled.div`
  margin-top: 16px;
  color: #64748b;
  font-size: 14px;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(180deg, #0a0f1c 0%, #1a1f2e 100%);
  padding: 24px;
`;

export default VendorBriefingPortal;